import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useExternalTokens, SearchResult } from "@/hooks/use-external-tokens";
import { useDebounce } from "@/hooks/use-debounce";
import { Search, Import, TrendingUp, Verified, Loader2, Zap, Coins, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

export function TokenSearch() {
  const navigate = useNavigate();
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    trendingTokens,
    loadingTrending,
    importToken,
    isImporting,
  } = useExternalTokens();

  const [inputValue, setInputValue] = useState("");
  const debouncedQuery = useDebounce(inputValue, 300);
  useEffect(() => setSearchQuery(debouncedQuery), [debouncedQuery, setSearchQuery]);

  const isDebouncing = inputValue !== debouncedQuery;

  const [importingId, setImportingId] = useState<string | null>(null);
  const [searchImageLoaded, setSearchImageLoaded] = useState<Record<string, boolean>>({});
  const [trendingImageLoaded, setTrendingImageLoaded] = useState<Record<string, boolean>>({});

  const handleResultClick = async (result: SearchResult) => {
    if (result.type === 'platform') {
      // Navigate directly to platform coin
      navigate(`/coin/${result.id}`);
    } else {
      // Import external token then navigate
      setImportingId(result.id);
      try {
        const imported = await importToken(result.address!);
        navigate(`/coin/${imported.id}`);
      } catch (error) {
        console.error('Import error:', error);
      } finally {
        setImportingId(null);
      }
    }
  };

  const handleTrendingClick = async (mint: string) => {
    setImportingId(mint);
    try {
      const result = await importToken(mint);
      navigate(`/coin/${result.id}`);
    } finally {
      setImportingId(null);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return '-';
    if (price < 0.00001) return `$${price.toExponential(2)}`;
    if (price < 1) return `$${price.toFixed(6)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatMarketCap = (mc?: number) => {
    if (!mc) return '-';
    if (mc >= 1e9) return `$${(mc / 1e9).toFixed(1)}B`;
    if (mc >= 1e6) return `$${(mc / 1e6).toFixed(1)}M`;
    if (mc >= 1e3) return `$${(mc / 1e3).toFixed(1)}K`;
    return `$${mc.toFixed(0)}`;
  };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Search by name, symbol, or paste Solana address..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="pl-12 h-14 text-lg bg-card border-border"
          aria-label="Search tokens by name, symbol, or address"
        />
        {(isSearching || isDebouncing) && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" />
        )}
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Search Results ({searchResults.length})
          </h3>
          <div className="grid gap-2 max-h-96 overflow-y-auto">
            {searchResults.map((result) => (
              <Card
                key={result.id}
                tabIndex={0}
                role="button"
                aria-label={`Trade ${result.symbol}`}
                className="hover:bg-secondary/50 transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => handleResultClick(result)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleResultClick(result);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = e.currentTarget.nextElementSibling as HTMLElement;
                    if (next) next.focus();
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = e.currentTarget.previousElementSibling as HTMLElement;
                    if (prev) prev.focus();
                  }
                }}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {result.logoURI ? (
                      <div className="relative w-10 h-10 rounded-full overflow-hidden">
                        {!searchImageLoaded[result.id] && <Skeleton className="absolute inset-0" />}
                        <img
                          src={result.logoURI}
                          alt={result.symbol}
                          className={cn("w-full h-full object-cover", !searchImageLoaded[result.id] && "opacity-0")}
                          onLoad={() => setSearchImageLoaded(prev => ({ ...prev, [result.id]: true }))}
                          onError={(e) => {
                            setSearchImageLoaded(prev => ({ ...prev, [result.id]: true }));
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-foreground">
                          {result.symbol.slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{result.symbol}</span>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-xs",
                            result.type === 'platform' 
                              ? "bg-primary/10 text-primary" 
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {result.type === 'platform' ? (
                            <>
                              <Coins className="w-3 h-3 mr-1" />
                              Platform
                            </>
                          ) : (
                            <>
                              <ExternalLink className="w-3 h-3 mr-1" />
                              External
                            </>
                          )}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">{result.name}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{formatPrice(result.price)}</div>
                    {result.priceChange24h !== undefined && (
                      <div className={cn(
                        "text-xs font-mono",
                        result.priceChange24h >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {result.priceChange24h >= 0 ? '+' : ''}{result.priceChange24h.toFixed(2)}%
                      </div>
                    )}
                  </div>
                  {importingId === result.id && (
                    <Loader2 className="w-4 h-4 animate-spin text-primary ml-2" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {inputValue.length >= 2 && !isSearching && searchResults.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <Search className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Tokens Found</h3>
          <p className="text-muted-foreground max-w-sm">
            Try a different search term or paste a token address.
          </p>
        </div>
      )}

      {/* Trending Tokens Section */}
      {inputValue.length < 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Trending on Solana</h3>
            </div>
            <Badge variant="outline" className="bg-primary/10">
              <Zap className="w-3 h-3 mr-1" />
              Live Data
            </Badge>
          </div>

          {loadingTrending ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(6).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-36 rounded-xl" />
              ))}
            </div>
          ) : trendingTokens.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingTokens.map((token, index) => (
                <Card
                  key={token.mint}
                  tabIndex={0}
                  role="button"
                  aria-label={`Trade ${token.symbol}`}
                  className="hover:border-primary/50 transition-colors cursor-pointer group focus-visible:ring-2 focus-visible:ring-primary"
                  onClick={() => handleTrendingClick(token.mint)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTrendingClick(token.mint);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = e.currentTarget.nextElementSibling as HTMLElement;
                      if (next) next.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = e.currentTarget.previousElementSibling as HTMLElement;
                      if (prev) prev.focus();
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {token.logoURI ? (
                            <div className="relative w-10 h-10 rounded-full overflow-hidden">
                              {!trendingImageLoaded[token.mint] && <Skeleton className="absolute inset-0" />}
                              <img
                                src={token.logoURI}
                                alt={token.symbol}
                                className={cn("w-full h-full object-cover", !trendingImageLoaded[token.mint] && "opacity-0")}
                                onLoad={() => setTrendingImageLoaded(prev => ({ ...prev, [token.mint]: true }))}
                                onError={(e) => {
                                  setTrendingImageLoaded(prev => ({ ...prev, [token.mint]: true }));
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                              <span className="text-sm font-bold text-primary-foreground">
                                {token.symbol.slice(0, 2)}
                              </span>
                            </div>
                          )}
                          <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold group-hover:text-primary transition-colors">
                            {token.symbol}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {token.name}
                          </div>
                        </div>
                      </div>
                      <div
                        className={cn(
                          "text-sm font-mono font-semibold",
                          token.priceChange24h >= 0 ? "text-success" : "text-destructive"
                        )}
                      >
                        {token.priceChange24h >= 0 ? "+" : ""}
                        {token.priceChange24h.toFixed(2)}%
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Price</span>
                        <span className="font-mono">{formatPrice(token.price)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Market Cap</span>
                        <span className="font-mono">{formatMarketCap(token.marketCap)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">24h Volume</span>
                        <span className="font-mono">{formatMarketCap(token.volume24h)}</span>
                      </div>
                    </div>

                    <Button
                      size="sm"
                      className="w-full mt-3 opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={importingId === token.mint}
                    >
                      {importingId === token.mint ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Trade Now
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Unable to load trending tokens</p>
              <p className="text-sm mt-1">Try searching for a specific token above</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
